import { Component, type ReactNode, type ErrorInfo } from 'react';
import { useLocation, type Location } from 'react-router-dom';
import { ErrorPage } from './ErrorPage';
import { reportError } from '@/lib/errorReporter';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  /** Current router location, injected by the exported wrapper below. */
  location: Location;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  /** Pathname of the last render — used to detect navigation. */
  lastPathname: string;
}

/**
 * Error Boundary — catches render errors anywhere below it and shows a recovery
 * page instead of a blank/broken screen.
 *
 * Recovery without a manual F5 comes from two things:
 *
 *  - "Refresh Page" hard-reloads the current URL. The boundary deliberately
 *    does NOT pass `onRetry` to ErrorPage, so the button falls back to
 *    `window.location.reload()` — rebooting on the page the user was on, rather
 *    than a soft state reset that just re-renders the same broken route and
 *    re-throws.
 *  - Navigating to a different route clears the latched error (see
 *    getDerivedStateFromProps), so the destination renders normally. The reset
 *    is passive: it never redirects on its own, so "Go Home", a header link or
 *    browser Back all work, and navigating back to the broken URL simply
 *    re-attempts that page.
 *
 * Previously `hasError` latched until a full document reload, which is why both
 * the "Refresh Page" and "Go Home" buttons appeared to do nothing — only F5
 * reconstructed the boundary and recovered.
 */
class ErrorBoundaryInner extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, lastPathname: props.location.pathname };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  static getDerivedStateFromProps(
    props: ErrorBoundaryProps,
    state: ErrorBoundaryState
  ): Partial<ErrorBoundaryState> | null {
    // Route changed since the last render: drop any latched error so the new
    // route can render. This runs in the render phase (before children render),
    // so there is no re-render-and-re-throw bounce. Staying on the broken URL
    // intentionally keeps the error visible — that case is recovered via the
    // "Refresh Page" hard reload instead.
    if (props.location.pathname !== state.lastPathname) {
      return {
        lastPathname: props.location.pathname,
        hasError: false,
        error: undefined,
      };
    }
    return null;
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    reportError({
      kind: 'react',
      error,
      componentStack: errorInfo.componentStack,
    });
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // No `onRetry` → ErrorPage's "Refresh Page" does a full window reload.
      return (
        <ErrorPage
          title="Something went wrong"
          message="We apologise for the inconvenience. Please try refreshing the page."
        />
      );
    }

    return this.props.children;
  }
}

/**
 * Location-aware wrapper so the class boundary can observe route changes.
 * Rendered inside <BrowserRouter> (see main.tsx), so useLocation() is valid.
 */
export function ErrorBoundary(props: Omit<ErrorBoundaryProps, 'location'>) {
  const location = useLocation();
  return <ErrorBoundaryInner {...props} location={location} />;
}
